import useGetNotifications from "@entities/brief/lib/hooks/notifications/useGetNotifications";
import useResolveNotification from "@entities/brief/lib/hooks/notifications/useResolveNotification";
import {
  BriefNotificationStatusType,
  IBriefNotification,
} from "@shared/lib/types/brief";
import { notification } from "@shared/ui";
import { Close } from "@shared/ui/icons";
import { useCallback, useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import { NotificationContent } from "./ui/NotificationContent";

const MAX_STACK = 3;

interface BriefNotificationsProps {
  briefId: string;
  selectedUserId: string;
  type: "priceReachout" | "campaignExecution" | "brief";
}

export default function BriefNotifications({
  briefId,
  selectedUserId,
  type,
}: BriefNotificationsProps) {
  const { notifications } = useGetNotifications(briefId, selectedUserId, type);

  const { resolveNotification } = useResolveNotification(briefId);
  const openKeysRef = useRef<Set<string>>(new Set());
  const dismissedIdsRef = useRef<Set<string>>(new Set());

  const unresolvedNotifications = notifications.filter(
    (n) =>
      n.status !== BriefNotificationStatusType.CompletedResolved &&
      n.status !== BriefNotificationStatusType.FailedResolved
  );

  // Clean up dismissed ids that are now resolved (mutation completed)
  const toRemove: string[] = [];
  dismissedIdsRef.current.forEach((id) => {
    const n = notifications.find((x) => x._id === id);
    if (
      !n ||
      n.status === BriefNotificationStatusType.CompletedResolved ||
      n.status === BriefNotificationStatusType.FailedResolved
    ) {
      toRemove.push(id);
    }
  });
  toRemove.forEach((id) => dismissedIdsRef.current.delete(id));

  const displayNotifications = unresolvedNotifications.filter(
    (n) => !dismissedIdsRef.current.has(n._id)
  );

  const [api, contextHolder] = notification.useNotification({
    stack: { threshold: MAX_STACK },
    maxCount: MAX_STACK,
    placement: "bottomRight",
    bottom: 80,
    duration: 0,
  });

  const handleCloseAndDestroy = useCallback(
    (notificationId: string) => {
      dismissedIdsRef.current.add(notificationId);
      api.destroy(notificationId);
      openKeysRef.current.delete(notificationId);
      resolveNotification(notificationId);
    },
    [resolveNotification, api]
  );

  useEffect(() => {
    const unresolvedIds = new Set(displayNotifications.map((n) => n._id));

    openKeysRef.current.forEach((key) => {
      if (!unresolvedIds.has(key)) {
        api.destroy(key);
        openKeysRef.current.delete(key);
      }
    });

    displayNotifications.forEach((n: IBriefNotification) => {
      openKeysRef.current.add(n._id);
      api.open({
        key: n._id,
        message: null,
        description: (
          <NotificationContent
            notification={n}
            briefId={briefId}
            onClose={handleCloseAndDestroy}
          />
        ),
        icon: null,
        closeIcon: (
          <button
            type="button"
            onClick={() => handleCloseAndDestroy(n._id)}
            className={styles.cardClose}
            aria-label="Close notification"
          >
            <Close size={14} />
          </button>
        ),
        duration: 0,
        className: styles.notification,
      });
    });
  }, [briefId, displayNotifications, api, handleCloseAndDestroy]);

  return <>{contextHolder}</>;
}
